import { getLink } from "@utils/linkUtils";
import { motion } from "framer-motion";
import { useSession } from "next-auth/react";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import FocusLock from "react-focus-lock";

import ContactChannels from "@/components/ContactChannels/ContactChannels";
import HamburgerMenu from "@/components/Header/HamburgerMenu";
import AnchorLink from "@/components/ui/AnchorLink/AnchorLink";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import { NavigationContext } from "@/context/NavigationContext";
import { TemplateContext } from "@/context/TemplateContext";
import { ctaClickTracking } from "@/dataLayer/dataLayer";
import useBreakPoint from "@/hooks/useBreakpoint";
import useScroll from "@/hooks/useScroll";
import { HeaderProps } from "@/types";

import SoftLogin from "../SoftLogin/SoftLogin";

//https://www.npmjs.com/package/react-focus-lock

const whiteMenu = {
  container: "top-0",
  containerBg: "bg-white",
  darkLogo: "block",
  lightLogo: "hidden",
  iconStroke: "stroke-black",
  hamburgerVariant: "dark",
};

const transparentMenu = {
  container: "top-0",
  containerBg: "bg-transparent",
  darkLogo: "hidden",
  lightLogo: "block",
  iconStroke: "stroke-white",
  hamburgerVariant: "light",
};

const HeaderMobileCorporate: FC<HeaderProps> = ({
  logoDark,
  logoLight,
  search,
  menu,
  personalArea,
  shortlist,
  contactChannels,
  getInTouch,
  settings,
}) => {
  const scroll = useScroll();
  const t = useTranslations();
  const locale = useLocale();
  const { isMd } = useBreakPoint("md");
  const { fixedHeader } = useContext(TemplateContext);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const menuItemRef = useRef<(HTMLDivElement | null)[]>([]);
  const headerMobileRef = useRef<HTMLDivElement>(null);

  const {
    mobileMenuOpen,
    setMobileMenuOpen,
    mobilePanelID,
    setMobilePanelID,
    settingsVisible,
    setSettingsVisible,
    setLanguagesVisible,
    languagesVisible,
    transitionComplete,
    setMobileHeaderHeight,
  } = useContext(NavigationContext);

  // scroll assume un valore tra -1 e 1 a seconda della direzione
  // 0 significa che sono al top
  const scrollClasses: Record<string, string> = useMemo(() => {
    if (scroll === 0) {
      if (fixedHeader) {
        return whiteMenu;
      } else {
        return whiteMenu;
      }
    } else if (scroll > 0) {
      return { ...whiteMenu, container: "top-[-100%]" };
    } else {
      return { ...whiteMenu, containerBg: "shadow-strong bg-white header-expanded" };
    }
  }, [scroll, fixedHeader]);

  const { data: session } = useSession();

  const callLink = useMemo(() => {
    return contactChannels?.find((item) => /phone/.test(`${item?.link?.icon}`));
  }, [contactChannels]);

  const languageCallback = useCallback(() => {
    if (settingsVisible) {
      setLanguagesVisible(languagesVisible);
    }
    setLanguagesVisible(!languagesVisible);
    ctaClickTracking(t("header.label.languages"), "HeaderMobileCorporate", "HeaderMobileCorporate");
  }, [settingsVisible, languagesVisible, setLanguagesVisible]);

  const settingsCallback = useCallback(() => {
    if (languagesVisible) {
      setLanguagesVisible(settingsVisible);
    }
    setSettingsVisible(!settingsVisible);
  }, [settingsVisible, languagesVisible, setLanguagesVisible, setSettingsVisible]);

  useEffect(() => {
    if (isMd === false) {
      setMobileHeaderHeight(headerMobileRef?.current?.offsetHeight ?? 0);
    }
  }, [headerMobileRef, setMobileHeaderHeight, isMd]);

  const variants = {
    open: { x: 0, display: "block" },
    close: { x: "100vw", transitionEnd: { display: "none" } },
  };
  const transition = {
    duration: 0.5,
  };
  return (
    <>
      <div
        id="header-mobile"
        ref={headerMobileRef}
        className={`${scrollClasses.container} ${scrollClasses.containerBg} ${fixedHeader ? "fixed" : "sticky"} w-full transition-all ease-in-out duration-500 backdrop-blur-md z-20 md:hidden`}
      >
        <div className="flex items-center justify-between px-m py-xs h-[60px]">
          <div className="flex items-center">
            <HamburgerMenu
              ref={hamburgerRef}
              variant={scrollClasses.hamburgerVariant}
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
              }}
            />
          </div>
          <AnchorLink href={`/${locale}/`} aria-label={t("button.label.homepage")} className="mr-[40px]">
            <Image
              className={`m-auto max-w-[133px] md:max-w-[170px] md:m-0 max-h-md object-contain ${scrollClasses.darkLogo}`}
              src={logoDark?.url}
              width={170}
              height={20}
              alt={logoDark?.title}
            />
            <Image
              className={`m-auto max-w-[133px] md:max-w-[170px] md:m-0 max-h-md object-contain ${scrollClasses.lightLogo}`}
              src={logoLight?.url}
              width={170}
              height={20}
              alt={logoDark?.title}
            />
          </AnchorLink>
          {contactChannels?.length ? (
            <div className="flex items-center">
              <ContactChannels
                variation="!w-[40px] !h-[40px] flex justify-center items-center"
                contactChannels={contactChannels}
                isCorporate={true}
              />{" "}
            </div>
          ) : (
            <div className="flex items-center"></div>
          )}
        </div>
      </div>
      {/* Main header mobile */}
      {/* Main menu mobile */}
      <motion.div
        id="mobile-panel-main"
        initial={variants.close}
        transition={transition}
        variants={variants}
        animate={mobileMenuOpen ? "open" : "close"}
        className={`fixed z-40 left-0 top-0 w-screen h-dvh bg-white `}
      >
        <FocusLock
          disabled={!mobileMenuOpen}
          className="h-full flex flex-col"
          autoFocus={true}
          group="main"
          returnFocus={() => {
            hamburgerRef?.current?.focus();
            return false;
          }}
        >
          <header className="flex justify-between items-center p-m">
            <p className="font-primary uppercase text-m overflow-hidden whitespace-nowrap text-ellipsis">{menu?.name}</p>
            <button
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
              }}
            >
              <Icon name="close" className="stroke-black" size="24" title="Close" />
            </button>
          </header>
          <div className="flex-1 overflow-auto py-xl px-m">
            <nav className="flex flex-col">
              {menu?.items?.map((item, index) => {
                const itemHref = item.menuItemCta ? getLink(item.menuItemCta, locale).toString() : "#";
                return (
                  item.title && (
                    <div
                      key={item.id}
                      ref={(el) => {
                        menuItemRef.current[index] = el;
                      }}
                    >
                      <Button
                        className="px-s w-full py-s no-underline font-medium flex justify-between items-center"
                        variant={ButtonVariant.TERTIARY}
                        size={ButtonSize.SMALL}
                        theme={ButtonTheme.BLACK}
                        type="button"
                        href={itemHref}
                        onClick={() => {
                          if ((!item.columns || item.columns.length === 0) && item.menuItemCta) {
                            // window.location.href = getLink(item.menuItemCta, locale).toString();
                          } else {
                            setMobilePanelID(item.id);
                          }
                          // setLastClick(index);
                        }}
                      >
                        <>
                          {item.title}
                          {item?.columns && item?.columns?.[0]?.links?.length > 0 && (
                            <Icon className="stroke-black" name="chevron-right" title="arrow" size="24" />
                          )}
                        </>
                      </Button>
                    </div>
                  )
                );
              })}
            </nav>
            {/* <hr className="my-xl border-white border-b border-b-gray-10" />
            <ContactChannels variation="!w-[40px] !h-[40px] flex justify-center items-center" contactChannels={contactChannels} isCorporate /> */}
          </div>
          {/* Language selection hide */}
          {settings?.settings?.preheader && (
            <footer className="shrink-0 flex justify-around border-t border-t-gray-10">
              <Button
                variant={ButtonVariant.TERTIARY}
                size={ButtonSize.SMALL}
                theme={ButtonTheme.BLACK}
                type="button"
                className="p-m"
                disabled={!transitionComplete}
                onClick={languageCallback}
              >
                <div className="flex flex-col items-center">
                  <Icon className="stroke-black" name="flag" size="20" />
                  <span className="font-secondary text-s mt-s">{t("header.label.languages")}</span>
                </div>
              </Button>
            </footer>
          )}
        </FocusLock>
      </motion.div>
      {/* Main menu mobile */}
      {/* Menu mobile panels */}
      {menu?.items?.map((item) => {
        const active = mobilePanelID && mobilePanelID === item.id;
        return (
          <motion.div
            id={`mobile-panel-mobile-${item.id}`}
            initial={variants.close}
            transition={transition}
            variants={variants}
            animate={active ? "open" : "close"}
            key={`subpanel-mobile-${item.id}`}
            className={`translate-x-[100%] fixed z-40 left-0 top-0 w-screen h-dvh bg-white `}
          >
            <FocusLock group="menu" disabled={!active} className="w-full h-full overflow-hidden">
              <div className="flex flex-col h-full">
                <header className="flex gap-s justify-between items-center p-m shrink-0">
                  <div className="flex items-center justify-center gap-m whitespace-nowrap text-ellipsis overflow-hidden">
                    <button
                      onClick={() => {
                        setMobilePanelID("");
                      }}
                    >
                      <Icon className="stroke-black" name="chevron-left" title="arrow" size="24" />
                    </button>
                    <p className="uppercase text-m font-primary font-medium overflow-hidden whitespace-nowrap text-ellipsis">{item.title}</p>
                  </div>
                  <button
                    onClick={() => {
                      setMobilePanelID("");
                      setMobileMenuOpen(false);
                    }}
                  >
                    <Icon name="close" className="stroke-black" size="24" title="Close" />
                  </button>
                </header>
                {item && item.columns && item.columns.length > 0 && (
                  <section className="flex-1 overflow-auto">
                    {item.menuItemCta ? (
                      <Link
                        href={getLink(item.menuItemCta, locale)}
                        onClick={(e) => {
                          const target = e?.target as HTMLAnchorElement;
                          if (window.location.pathname === target.getAttribute("href")) {
                            setMobilePanelID("");
                            setMobileMenuOpen(false);
                          }
                        }}
                        className="text-m px-s py-s mt-m mb-l w-full no-underline flex justify-between items-center font-secondary font-medium"
                      >
                        {item.menuItemCta.label}
                      </Link>
                    ) : (
                      <p className="text-m px-s py-s mt-m mb-l w-full no-underline flex justify-between items-center font-secondary font-medium">
                        {item.columns[0].title}
                      </p>
                    )}
                    <hr className="border-t-gray-10 mx-s mb-l sticky top-0" />
                    {item.columns.map((column, index) => {
                      return (
                        <div key={`column-${index}-${column.id}`}>
                          {/* <p className="px-s text-s uppercase text-gray-50 font-primary font-medium">{column.title}</p> */}
                          <ul>
                            {column.links?.map((link, lindex) => {
                              return (
                                <li key={lindex}>
                                  <Button
                                    className={`text-m px-s py-s mb-xs w-full no-underline flex justify-between items-center font-secondary font-medium`}
                                    variant={ButtonVariant.TERTIARY}
                                    size={ButtonSize.SMALL}
                                    theme={ButtonTheme.BLACK}
                                    type="button"
                                    href={getLink(link, locale)}
                                    onClick={(e) => {
                                      const target = e?.target as HTMLButtonElement;
                                      if (window.location.pathname === target.getAttribute("href")) {
                                        setMobilePanelID("");
                                        setMobileMenuOpen(false);
                                      }
                                    }}
                                  >
                                    {link.label}
                                  </Button>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      );
                    })}
                  </section>
                )}
              </div>
            </FocusLock>
          </motion.div>
        );
      })}
      {/* Menu mobile panels */}
    </>
  );
};
export default HeaderMobileCorporate;
